import { Laptop, Smartphone, Tablet, HelpCircle } from 'lucide-react';
import { Alert, Badge, Button, Card, PageHeader, Spinner } from '../../components/ui.tsx';
import { useAuth } from '../auth/AuthContext.tsx';
import { useDialog } from '../../components/Dialog.tsx';
import { useToast } from '../../components/Toast.tsx';
import { ApiError } from '../../lib/api.ts';
import { formatDateTime, formatRelative, pluralise } from '../../lib/display.ts';
import {
  useRevokeOtherSessions,
  useRevokeSession,
  useSessions,
  type ActiveSession,
} from '../../lib/profile-queries.ts';
import { describeAddress, parseUserAgent, type DeviceKind } from '../../lib/user-agent.ts';

/**
 * Where this account is currently signed in.
 *
 * The question this page exists to answer is "is any of this not me?" — so
 * the device, the browser and the address are the headline, and the internal
 * session id never appears at all. It is also the only place a compromised
 * account shows itself before money moves.
 */
export function SessionsPage() {
  const sessions = useSessions();
  const revoke = useRevokeOtherSessions();
  const revokeOne = useRevokeSession();
  const dialog = useDialog();
  const toast = useToast();
  const { logout } = useAuth();

  if (sessions.isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 text-body-sm text-muted">
        <Spinner /> Loading your sign-ins…
      </div>
    );
  }

  if (sessions.isError) return <Alert>Could not load your sign-ins.</Alert>;

  const list = sessions.data ?? [];
  const others = list.filter((session) => !session.isCurrent);

  async function signOutEverywhere() {
    const confirmed = await dialog.confirm({
      title: 'Sign out everywhere?',
      message:
        'Every device signs out, including this one. You will need to sign in again with your password.',
      details: [`${pluralise(others.length, 'other session')} will end`],
      confirmLabel: 'Sign out everywhere',
      tone: 'danger',
    });
    if (!confirmed) return;

    try {
      await revoke.mutateAsync();
      toast.success('Signed out everywhere');
    } catch {
      toast.error('Could not sign out the other sessions');
    }
  }

  /**
   * End one device.
   *
   * Ending the device you are holding is allowed — it is the quickest way to
   * sign out of a shared computer from the screen you are already on — but it
   * has to finish locally too. The server has cleared the refresh cookie; the
   * client still holds an access token in memory, and without dropping it the
   * app would sit on a dead session until the next request happened to fail.
   */
  async function signOutDevice(session: ActiveSession) {
    const agent = parseUserAgent(session.userAgent);

    const confirmed = await dialog.confirm({
      title: session.isCurrent ? 'Sign out of this device?' : `Sign out of ${agent.summary}?`,
      message: session.isCurrent
        ? 'You will need to sign in again on this computer.'
        : 'That device is signed out immediately and will need your password to get back in.',
      details: session.isCurrent
        ? []
        : [
            describeAddress(session.ipAddress),
            `Last used ${session.lastUsedAt ? formatRelative(session.lastUsedAt) : 'not since signing in'}`,
          ],
      confirmLabel: 'Sign out',
      tone: 'danger',
    });
    if (!confirmed) return;

    try {
      const { wasCurrent } = await revokeOne.mutateAsync(session.id);
      if (wasCurrent) {
        await logout();
        return;
      }
      toast.success(`${agent.summary} signed out`);
    } catch (caught) {
      toast.error(
        'Could not sign that device out',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  return (
    <div className="h-full overflow-y-auto pb-10">
      <PageHeader
        title="Sign-ins"
        description="Every device your account is currently signed in on. If you do not recognise one, sign out everywhere and change your password."
        actions={
          list.length > 1 ? (
            <Button
              variant="danger"
              onClick={() => void signOutEverywhere()}
              disabled={revoke.isPending}
            >
              {revoke.isPending ? <Spinner label="Signing out" /> : 'Sign out everywhere'}
            </Button>
          ) : undefined
        }
      />

      <div className="mt-4 space-y-3">
        {list.map((session) => (
          <SessionCard
            key={session.id}
            session={session}
            onSignOut={() => void signOutDevice(session)}
            busy={revokeOne.isPending}
          />
        ))}

        {list.length === 1 ? (
          <Card>
            <p className="text-body-sm text-muted">
              This is the only device signed in to your account.
            </p>
          </Card>
        ) : null}
      </div>
    </div>
  );
}

function SessionCard({
  session,
  onSignOut,
  busy,
}: {
  session: ActiveSession;
  onSignOut: () => void;
  busy: boolean;
}) {
  const agent = parseUserAgent(session.userAgent);

  return (
    <div
      className={`flex flex-wrap items-start gap-4 rounded-xl border bg-surface p-4 ${
        session.isCurrent ? 'border-[var(--blue-300)] bg-blue-50' : 'border-border-strong'
      }`}
    >
      <span
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${
          session.isCurrent ? 'bg-blue-100 text-blue-700' : 'bg-sunken text-muted'
        }`}
        aria-hidden
      >
        <DeviceGlyph kind={agent.device} />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-body font-medium text-ink">{agent.summary}</h2>
          {session.isCurrent ? <Badge tone="info">This device</Badge> : null}
        </div>

        <dl className="mt-2 grid gap-x-6 gap-y-1 text-meta sm:grid-cols-2">
          <Row label="Address" value={describeAddress(session.ipAddress)} />
          <Row
            label="Device"
            value={`${labelFor(agent.device)}${agent.os === 'Unknown' ? '' : ` · ${agent.os}`}`}
          />
          <Row
            label="Signed in"
            value={`${formatDateTime(session.createdAt)} (${formatRelative(session.createdAt)})`}
          />
          <Row
            label="Last used"
            value={
              session.lastUsedAt
                ? `${formatDateTime(session.lastUsedAt)} (${formatRelative(session.lastUsedAt)})`
                : 'Not used since signing in'
            }
          />
        </dl>

        <p className="mt-2 text-micro text-subtle">Expires {formatDateTime(session.expiresAt)}</p>
      </div>

      <div className="shrink-0">
        <Button onClick={onSignOut} disabled={busy}>
          {session.isCurrent ? 'Sign out here' : 'Sign out'}
        </Button>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap gap-2">
      <dt className="shrink-0 text-muted">{label}</dt>
      <dd className="min-w-0 truncate text-body" title={value}>
        {value}
      </dd>
    </div>
  );
}

function DeviceGlyph({ kind }: { kind: DeviceKind }) {
  const props = { size: 22, strokeWidth: 1.6, absoluteStrokeWidth: true } as const;
  if (kind === 'mobile') return <Smartphone {...props} />;
  if (kind === 'tablet') return <Tablet {...props} />;
  if (kind === 'desktop') return <Laptop {...props} />;
  return <HelpCircle {...props} />;
}

function labelFor(kind: DeviceKind): string {
  return kind === 'unknown' ? 'Unrecognised device' : kind[0]!.toUpperCase() + kind.slice(1);
}
